// The invoices and credit notes the invoicing service issued this organization, and their PDFs.
// `tenancy.billing.manage` on all three.
//
// **Nothing here keeps a document**: the listing is a proxy over every document of this
// organization's customer, credit notes included — and those hang off a refund, so a walk of this
// product's own top-up rows would miss them. An organization with no customer reads an empty page
// without the backend dialling anything.

import { api, withQuery } from '../client'
import type { FileBody } from '../client'
import type { components, operations } from '../schema'

export type BillingDocumentEntry = components['schemas']['BillingDocumentEntry']
export type BillingDocumentPage = components['schemas']['Page_BillingDocumentEntry']

/**
 * The walk. **`after` is the invoicing service's own cursor and travels through unchanged**, so a
 * page's `next` is sent back as it came and never re-minted here.
 */
export type BillingDocumentsQuery = NonNullable<
  operations['orgs_billing_documents_list']['parameters']['query']
>

/** A page of documents. `kind` is `invoice` or `credit_note`; omitted reads both. */
export function listBillingDocuments(
  query: BillingDocumentsQuery = {},
  signal?: AbortSignal,
): Promise<BillingDocumentPage> {
  return api.get<BillingDocumentPage>(
    withQuery('/orgs/current/billing-documents', query),
    undefined,
    signal,
  )
}

/** One document. `pdf_available` says whether its bytes answer yet. */
export function readBillingDocument(
  documentId: string,
  signal?: AbortSignal,
): Promise<BillingDocumentEntry> {
  return api.get<BillingDocumentEntry>(
    `/orgs/current/billing-documents/${encodeURIComponent(documentId)}`,
    undefined,
    signal,
  )
}

/**
 * Its stored bytes, as an attachment named after the document's number.
 *
 * Read through the client rather than linked: the call carries `X-Org-Id`, which an `<a href>`
 * cannot. **409 is the one refusal worth retrying** — the renderer runs after issuance, so for a
 * few seconds the document exists and its PDF does not.
 */
export function readBillingDocumentPdf(
  documentId: string,
  signal?: AbortSignal,
): Promise<FileBody> {
  return api.getFile(
    `/orgs/current/billing-documents/${encodeURIComponent(documentId)}/pdf`,
    signal,
  )
}
